import React, { useState } from "react";
import Box from "component/layoutComponents/Box/Box";
import { useTheme } from "styled-components";
import {
  StyledOrderLink,
  StyledOrderCardRightHeader,
  StyledOrderNumber,
  StyledProductImage,
  StyledRowProduct,
  StyledCardContent,
} from "./index.style";
import {
  useMediaQuery,
  SwipeableDrawer,
  Avatar,
  Menu,
  Theme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { StyledCardItems } from "./index.style";
import DefaultUptimeTheme from "component/theme/defaultTheme";
import EyeBrow from "../eyeBrow/eyeBrow";
import { IconButton } from "../ButtonComponents";
import Typography from "../Typography/Typography";
import { getAvatarName } from "utils/functions";
import { mapOrderStatus } from "../OrderRequestDetail/utils";
import { StyledIcon } from "../../commonComponents/Icon/icon.style";
import InfoIcon from "@mui/icons-material/Info";
import { ORDER_STATUS, APPROVAL_STATUS } from "../OrderRequestDetail/constant";
import ApprovalCard from "./components/ApprovalCard";
import RequestCard from "./components/RequestCard";
import MenuContent from "./components/MenuContent";
import { StyledTooltip } from "component/cardComponent/pcCard/pcCard.style";
import OrderItemCardSkeleton from "./components/Skeleton";
import PaperWrapper from "./components/PaperWrapper";
import DateUtils from "utils/date";
import { useCustomLayout } from "component/layoutComponents/CustomLayout/CustomLayoutContext";

export interface IOrderItemCardProps {
  content: any;
  currentUser?: any;
  isApprovalPage?: boolean;
  isTicketInManageLandingPage?: boolean;
  actionsTicket?: any;
  onViewDetails?: (orderedId: string) => void;
  onCancelOrder?: (orderItem: Record<string, any>) => void;
  isShowLabelTicket?: boolean;
  labelTicket?: string;
  isShowApproval?: boolean;
  isShowOrder?: boolean;
  onDecline?: (orderedId: string) => void;
  onApprove?: (orderedId: string) => void;
  locale?: any;
  statusTicketLocale?: string;
  stageTicketLocale?: string;
  pendingApprovalDelegatedLocale?: string;
}

const OrderItemCard = (props: IOrderItemCardProps) => {
  const {
    content,
    currentUser,
    isApprovalPage,
    isTicketInManageLandingPage = false,
    actionsTicket,
    onViewDetails = () => {},
    onCancelOrder = () => {},
    isShowLabelTicket = false,
    labelTicket,
    isShowApproval = true,
    isShowOrder = true,
    onApprove = () => {},
    onDecline = () => {},
    locale,
    statusTicketLocale,
    stageTicketLocale,
    pendingApprovalDelegatedLocale
  } = props;
  const theme = useTheme() as typeof DefaultUptimeTheme;
  const isSmallUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));
  const isSmallDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [openDrawer, setOpenDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openActionMenu = Boolean(anchorEl);
  const orderDetailsStatus = isApprovalPage
    ? content?.approval?.approver_type === APPROVAL_STATUS.DELEGATED
      ?  content?.order_detail?.meta_status?.status +`_${APPROVAL_STATUS.DELEGATED}`
      : content?.order_detail?.meta_status?.status || content?.order_detail?.status
    : content?.status || content?.state;
  const { statusText, statusColors } = mapOrderStatus(orderDetailsStatus);
  const createdOn = !isApprovalPage
    ? (content?.createDate || content?.createdOn)
    : content?.order_detail?.createdOn;
  const products = !isApprovalPage
    ? content?.products
    : content?.order_detail?.products;
  const stageTicket = content?.originalState;
  const { statusText: statusTextTicket, statusColors: statusColorsTicket } =
    mapOrderStatus(stageTicket);

  const showMoreActionButton =
    orderDetailsStatus === ORDER_STATUS.PENDING_APPROVAL ||
    orderDetailsStatus === ORDER_STATUS.PENDING_APPROVAL_DELEGATED;

  const customLayoutConfig = useCustomLayout();
  const { isUseHyperlinkStyle } = customLayoutConfig;

  const handleClickActionMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (isSmallUp && showMoreActionButton) {
      setAnchorEl(event.currentTarget);
      return;
    }
    setOpenDrawer(true);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  return (
    <PaperWrapper data-ritm_number={content?._id}>
      {isShowLabelTicket && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          px={4}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={700}
            lineHeight="32px"
            sx={{ textTransform: "uppercase" }}
            id="lbl-orderitemcard-label_ticket"
          >
            {labelTicket}
          </Typography>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        px={4}
      >
        <StyledOrderLink onClick={() => onViewDetails(content?._id)} sx={{ flex: '1'}} tabIndex={0} role="link">
          <StyledTooltip title={content?.shortDescription ||
              content?.requestItem?.shortDescription ||
              `${locale?.orderLabel} #${(content?._id)?.slice(0, 5)}`}
              placement="bottom-start">
            <StyledOrderNumber
              variant="h4"
              fontSize={`${theme.uptimeFontSizes.desktop.fz9}px`}
              fontWeight={600}
              color={theme.uptimeColors.grayScales.darkerGray}
              lineHeight={`${theme.uptimeLineHeights.desktop.lh9}px`}
              textAlign="left"
              maxlines={1}
              id="lbl-orderitemcard-short_description"
            >
              {content?.shortDescription ||
                content?.requestItem?.shortDescription ||
                `${locale?.orderLabel} #${(content?._id)?.slice(0, 5)}`}
            </StyledOrderNumber>
          </StyledTooltip>
        </StyledOrderLink>
        <StyledOrderCardRightHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
        >
          <StyledOrderLink
            onClick={(e) => {
              e.preventDefault();
              onViewDetails(content?._id);
            }}
            arial-label="View Details"
            sx={{ marginLeft: '10px'}}
            id="lnk-orderitemcard-view_detail"
            data-ritm_number={content?._id}
            href="#"
            tabIndex={0}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.uptimeColors?.generalColors?.button?.hyperlink?.enabled,
                '&:hover': {
                  color: theme.uptimeColors?.generalColors?.button?.hyperlink?.hover,
                  textDecoration: isUseHyperlinkStyle ? 'underline' : 'none'
                }
              }}
              fontWeight={600}
              display={{
                xxs: isTicketInManageLandingPage ? "block" : "none",
                xs: isTicketInManageLandingPage ? "block" : "none",
                md: "block",
              }}
              id="lbl-orderitemcard-view_detail"
            >
              {locale?.viewDetail}
            </Typography>
          </StyledOrderLink>
          {isSmallUp && showMoreActionButton && (
            <>
              <IconButton
                icon={MoreVertIcon}
                aria-label="View More Button"
                value="viewMoreButton"
                onClick={handleClickActionMenu}
                id="btn-orderitemcard-more"
              />
              <Menu
                anchorEl={anchorEl}
                open={openActionMenu}
                onClose={handleCloseActionMenu}
                disableScrollLock
              >
                <MenuContent
                  showCancelButton={showMoreActionButton}
                  isApprovalPage={isApprovalPage}
                  onCancelOrder={() => {
                    handleCloseActionMenu();
                    onCancelOrder(content);
                  }}
                  onViewDetails={() => onViewDetails(content?._id)}
                  onApprove={() => {
                    onApprove(content?._id);
                    setAnchorEl(null);
                  }}
                  onDecline={() => {
                    onDecline(content?._id);
                    setAnchorEl(null);
                  }}
                  locale={locale}
                />
              </Menu>
            </>
          )}
          {isSmallUp && isTicketInManageLandingPage && (
            <Box alignSelf="center" padding="0 12px">
              {actionsTicket}
            </Box>
          )}
        </StyledOrderCardRightHeader>
        {showMoreActionButton && <Box display={{ xxs: "block", sm: "none" }}>
          <IconButton
            icon={MoreVertIcon}
            aria-label="View More Button"
            onClick={() => {
              setOpenDrawer(true);
            }}
            id="btn-orderitemcard-more"
          />
          <SwipeableDrawer
            anchor="bottom"
            open={openDrawer}
            onClose={() => {
              setOpenDrawer(false);
            }}
            onOpen={() => {
              setOpenDrawer(true);
            }}
          >
            <MenuContent.Drawer
              showCancelButton={showMoreActionButton}
              isApprovalPage={isApprovalPage}
              onCancelOrder={() => onCancelOrder(content)}
              onViewDetails={() => onViewDetails(content?._id)}
              onApprove={() => onApprove(content?._id)}
              onDecline={() => onDecline(content?._id)}
              locale={locale}
            />
          </SwipeableDrawer>
        </Box>}
        {isSmallDown && isTicketInManageLandingPage && (
          <Box alignSelf="center" padding="0 12px">
            {actionsTicket}
          </Box>
        )}
      </Box>
      <Box
        display="flex"
        mb={4}
        px={4}
        flexDirection={{ xxs: "column", sm: "column", md: "row" }}
      >
        <Box
          mr={{
            xxs: 0,
            sm: 0,
            md: 4,
          }}
          display="flex"
          flexDirection={{ xxs: "row", sm: "row", md: "column" }}
          alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
        >
          <Box width={84} mr={{ xxs: 4, sm: 4, md: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              lineHeight="32px"
              textTransform="uppercase"
            >
              {locale?.status}
            </Typography>
          </Box>
          <EyeBrow
            size="small"
            color={statusColors.type}
            label={(isApprovalPage && content?.approval?.approver_type === APPROVAL_STATUS.DELEGATED) ? pendingApprovalDelegatedLocale : (statusTicketLocale || statusText)}
            variant="filled"
            sx={{
              backgroundColor: statusColors.backgroundColor,
              color: statusColors.color,
              fontWeight: 600,
              textTransform: "uppercase",
            }}
            id="lbl-orderitemcard-status"
          />
        </Box>
        {isShowApproval && (
          <Box
            mr={{
              xxs: 0,
              sm: 0,
              md: 5,
            }}
            display="flex"
            flexDirection={{ xxs: "row", sm: "row", md: "column" }}
            alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
          >
            <Box mr={{ xxs: 4, sm: 4, md: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
                lineHeight="32px"
                textTransform="uppercase"
              >
                {locale?.created}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" id="lbl-orderitemcard-created_on">
              {DateUtils.formatDate(createdOn, "ll")}
            </Typography>
          </Box>
        )}
        {isShowOrder && (
          <Box
            mr={{
              xxs: 0,
              sm: 0,
              md: 5,
            }}
            display="flex"
            flexDirection={{ xxs: "row", sm: "row", md: "column" }}
            alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
          >
            <Box mr={{ xxs: 4, sm: 4, md: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
                lineHeight="32px"
                textTransform="uppercase"
              >
                {locale?.updated}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" id="lbl-orderitemcard-update_date">
              {DateUtils.formatDate(content?.updateDate || content?.updatedOn || createdOn, "ll")}
            </Typography>
          </Box>
        )}
        <Box
          mr={{
            xxs: 0,
            sm: 0,
            md: 5,
          }}
          display="flex"
          flexDirection={{ xxs: "row", sm: "row", md: "column" }}
          alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
        >
          {isShowOrder &&
            content?.status &&
            content.status?.toLowerCase() !== "pending" && (
              <>
                <Box mr={{ xxs: 4, sm: 4, md: 0 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={700}
                    lineHeight="32px"
                    textTransform="uppercase"
                  >
                    {locale?.place}
                  </Typography>
                </Box>
                <Typography color="text.secondary" variant="body2" id="lbl-orderitemcard-place">
                  {DateUtils.formatDate(createdOn, "ll")}
                </Typography>
              </>
            )}
          {(content?.number || content?.requestItem?.number) && (
            <>
              <Box mr={{ xxs: 4, sm: 4, md: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                  textTransform="uppercase"
                >
                  {locale?.number}
                </Typography>
              </Box>
              <Typography color="text.secondary" variant="body2" id="lbl-orderitemcard-number">
                {content?.number || content?.requestItem?.number}
              </Typography>
            </>
          )}
        </Box>
        {content?.originalState && (
          <Box
            mr={{
              xxs: 0,
              sm: 0,
              md: 5,
            }}
            display="flex"
            flexDirection={{ xxs: "row", sm: "row", md: "column" }}
            alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
          >
            <Box
              display="flex"
              alignItems="center"
              mr={{ xxs: 4, sm: 4, md: 0 }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
                lineHeight="32px"
                pr={0.5}
                textTransform="uppercase"
              >
                {locale?.stage}
              </Typography>
              <StyledIcon
                styleIcon={{
                  fontSize: `${theme.uptimeFontSizes.desktop.fz4}`,
                  color: theme.uptimeColors.grayScales.darkerGray,
                  width: theme.uptimeSpace[7],
                  height: theme.uptimeSpace[7],
                  marginTop: "-2px",
                }}
                id="img-orderitemcard-stage"
              >
                <InfoIcon />
              </StyledIcon>
            </Box>
            <EyeBrow
              size="small"
              color={statusColorsTicket?.type}
              label={stageTicketLocale || statusTextTicket}
              variant="filled"
              sx={{
                backgroundColor: statusColorsTicket.backgroundColor,
                color: statusColorsTicket.color,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
              id="lbl-orderitemcard-status"
            />
          </Box>
        )}
        {content?.status && content?.status?.toLowerCase() !== "completed" && (
          <>
            <Box
              mr={{
                xxs: 0,
                sm: 0,
                md: 5,
              }}
              display="flex"
              flexDirection={{ xxs: "row", sm: "row", md: "column" }}
              alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
            >
              <Box mr={{ xxs: 4, sm: 4, md: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  fontWeight={700}
                  lineHeight="32px"
                  textTransform="uppercase"
                >
                  {locale?.requester}
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems={{ xxs: "center", sm: "center", md: "flex-start" }}
                height="100%"
              >
                <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                  <Typography variant="caption" id="img-orderitemcard-avatar">
                    {getAvatarName(currentUser?.name)}
                  </Typography>
                </Avatar>
                <Typography variant="body2" color="text.secondary">
                  {content?.requested_by?.name || currentUser?.name}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
      {products?.length > 0 && (
        <StyledCardContent>
          <StyledRowProduct>
              {products.map((item: any) => {
                return (
                  <StyledCardItems>
                    <StyledProductImage>
                      <img
                        src={item?.pictures[0]?.small}
                        alt={item?.genericName}
                        title={item?.genericName}
                        id="img-orderitemcard-picture"
                      />
                    </StyledProductImage>
                    <EyeBrow
                      color="default"
                      label={item.quantity}
                      variant="filled"
                      id="lbl-orderitemcard-quantity"
                    />
                  </StyledCardItems>
                );
              })}
          </StyledRowProduct>
        </StyledCardContent>
      )}
      {/* <Box px={4} pt={2} display="flex" alignItems="center">
        <StyledOrderItemFooterButton display="flex" alignItems="center" mr={4}>
          <StyledIcon
            styleIcon={{
              fontSize: `${theme.uptimeFontSizes.desktop.fz4}px`,
              color: theme.uptimeColors.grayScale[17],
              width: theme.uptimeSpace[7],
              height: theme.uptimeSpace[7],
            }}
          >
            <CommentRounded />
          </StyledIcon>
          <Typography variant='caption' color="text.secondary" pt="6px" pl={0.5}>
            4
          </Typography>
        </StyledOrderItemFooterButton>
        <StyledOrderItemFooterButton display="flex" alignItems="center">
          <StyledIcon
            styleIcon={{
              fontSize: `${theme.uptimeFontSizes.desktop.fz4}px`,
              color: theme.uptimeColors.grayScale[17],
              width: theme.uptimeSpace[7],
              height: theme.uptimeSpace[7],
            }}
          >
            <AttachmentRounded />
          </StyledIcon>
          <Typography variant='caption' color="text.secondary" pt="6px" pl={0.5}>
            1
          </Typography>
        </StyledOrderItemFooterButton>
      </Box> */}
    </PaperWrapper>
  );
};

OrderItemCard.Approval = ApprovalCard;
OrderItemCard.Request = RequestCard;
OrderItemCard.Skeleton = OrderItemCardSkeleton;

export default OrderItemCard;
